import axios from 'axios';
import i18n from './i18n';
import * as constants from '../constants/common';
import { showToast } from '../helpers/showToast';
import { isArray } from 'lodash';

const servicesInstance = axios.create({});
const smeInstance = axios.create({});
const certificationInstance = axios.create({});
const printInstance = axios.create({});
const otpInstance = axios.create({});
const statisticsInstance = axios.create({});
const onboardingInstance = axios.create({});
const onboardingAuthInstance = axios.create({});
const authInstance = axios.create({});

let refreshTries = 0;
const REFRESH_TOKEN_RETRY_LIMIT = 3;

export const initiateServiceInstance = ({
  API_URL,
  ONBOARDING_GRAVITEE_KEY,
}) => {
  servicesInstance.defaults.baseURL = API_URL;
  servicesInstance.defaults.headers['X-Gravitee-Api-Key'] =
    ONBOARDING_GRAVITEE_KEY;
};

export const initiateSmeInstance = ({ SME_URL, SME_GRAVITEE_KEY }) => {
  smeInstance.defaults.baseURL = SME_URL;
  smeInstance.defaults.headers['X-Gravitee-Api-Key'] = SME_GRAVITEE_KEY;
};

export const initiateCertificationInstance = ({
  API_URL,
  CERTIFICATION_GRAVITEE_KEY,
}) => {
  certificationInstance.defaults.baseURL = API_URL;
  certificationInstance.defaults.headers['X-Gravitee-Api-Key'] =
    CERTIFICATION_GRAVITEE_KEY;
};

export const initiatePrintInstance = ({ PRINT_URL }) => {
  printInstance.defaults.baseURL = PRINT_URL;
  printInstance.defaults.responseType = 'arraybuffer';
};

export const initiateOTPInstance = ({ OTP_URL, OTP_GRAVITEE_KEY }) => {
  otpInstance.defaults.baseURL = OTP_URL;
  otpInstance.defaults.headers['X-Gravitee-Api-Key'] = OTP_GRAVITEE_KEY;
  otpInstance.defaults.headers['request-from'] = 'otp-service';
  otpInstance.defaults.headers.Accept = 'application/json';
};

export const initiateStatisticsInstance = ({
  STATISTICS_API_URL,
  STATISTICS_GRAVITEE_KEY,
}) => {
  statisticsInstance.defaults.baseURL = STATISTICS_API_URL;
  statisticsInstance.defaults.headers['X-Gravitee-Api-Key'] =
    STATISTICS_GRAVITEE_KEY;
};

export const initiateOnboardingInstance = ({
  ONBOARDING_URL,
  ONBOARDING_GRAVITEE_KEY,
}) => {
  onboardingInstance.defaults.baseURL = ONBOARDING_URL;
  onboardingInstance.defaults.headers['X-Gravitee-Api-Key'] =
    ONBOARDING_GRAVITEE_KEY;
};

export const initiateOnboardingAuthInstance = ({
  ONBOARDING_URL,
  ONBOARDING_AUTH_GRAVITEE_KEY,
}) => {
  onboardingAuthInstance.defaults.baseURL = ONBOARDING_URL;
  onboardingAuthInstance.defaults.headers['X-Gravitee-Api-Key'] =
    ONBOARDING_AUTH_GRAVITEE_KEY;
};

export const initiateAuthInstance = ({ API_URL, OIDC_GRAVITEE_KEY }) => {
  authInstance.defaults.baseURL = API_URL;
  authInstance.defaults.headers['X-Gravitee-Api-Key'] = OIDC_GRAVITEE_KEY;
  authInstance.defaults.withCredentials = true;
};

const refreshAccessToken = () => {
  const refreshToken = localStorage.getItem(constants.REFRESH_TOKEN_KEY);
  const token = localStorage.getItem(constants.TOKEN_KEY);
  return authInstance.post(`${constants.AUTH_REFRESH_URL}`, {
    token,
    refreshToken,
  });
};

const _interceptResponseSuccess = (response) => {
  return response;
};

const handleSessionExpired = () => {
  // Clear all auth data
  localStorage.removeItem(constants.TOKEN_KEY);
  localStorage.removeItem(constants.REFRESH_TOKEN_KEY);
  localStorage.removeItem('uinfo');

  // Notify other tabs about session expiration using broadcast channel
  try {
    // eslint-disable-next-line no-undef
    const logoutChannel = new BroadcastChannel('auth_logout_channel');
    logoutChannel.postMessage('logout');
    logoutChannel.close();
  } catch (e) {
    // Fallback to localStorage event if BroadcastChannel is not supported
    localStorage.setItem('logout_event', Date.now().toString());
    localStorage.removeItem('logout_event');
  }

  // Show session expired message
  showToast({
    data: {
      variant: 'error',
      text: i18n.t(
        'session-expired',
        'Your session has expired. Please login again.',
      ),
    },
  });

  // Clear session and reload to redirect to login
  sessionStorage.clear();
  setTimeout(() => {
    window.location.reload();
  }, 1000);
};

const _interceptResponseError = async (error, instance) => {
  const refreshToken = localStorage.getItem(constants.REFRESH_TOKEN_KEY);
  const token = localStorage.getItem(constants.TOKEN_KEY);

  if (
    error.response &&
    error.response.status === 401 &&
    refreshToken &&
    token
  ) {
    // Access token has expired, refresh it
    refreshTries += 1;
    if (refreshTries > REFRESH_TOKEN_RETRY_LIMIT) {
      refreshTries = 0;
      handleSessionExpired();
      return Promise.reject(new Error('Session expired'));
    }

    try {
      const newAccessToken = await refreshAccessToken();
      // Update the request headers with the new access token

      localStorage.setItem(
        constants.TOKEN_KEY,
        newAccessToken.data.accessToken,
      );
      localStorage.setItem(
        constants.REFRESH_TOKEN_KEY,
        newAccessToken.data.refreshToken,
      );
      const uinfo = JSON.parse(localStorage.getItem('uinfo'));
      uinfo.accessToken = newAccessToken.data.accessToken;
      uinfo.refreshToken = newAccessToken.data.refreshToken;
      localStorage.setItem('uinfo', JSON.stringify(uinfo));

      error.config.headers.Authorization = `Bearer ${newAccessToken.data.accessToken}`;

      // Reset retry counter on successful refresh
      refreshTries = 0;

      // Retry the original request
      return instance(error.config);
    } catch (refreshError) {
      // If refresh fails, retry with incremented counter
      return _interceptResponseError(error, instance);
    }
  }

  if (!error.response) {
    return Promise.reject(new Error('Internal Server Error'));
  }

  let errors = [];
  if (isArray(error?.response?.data)) {
    errors = error?.response?.data.map((err) => {
      return i18n.t('api_errors.' + err?.code, err?.message);
    });
  } else {
    const errorMessage = i18n.t(
      'api_errors.' + error?.response?.data?.code,
      error?.response?.data?.message,
    );
    errors.push(errorMessage);
  }

  if (
    error.response &&
    error.response.status !== 404 &&
    error.response.status !== 401
  ) {
    errors.forEach((errorMessage) => {
      showToast({
        data: {
          variant: 'error',
          text: errorMessage,
        },
      });
    });
  }

  return Promise.reject(new Error(error?.message || 'Error'));
};

const _interceptRequest = (request) => {
  const token = localStorage.getItem(constants.TOKEN_KEY);
  const language = i18n.language || 'ar';

  request.headers['Accept-Language'] = language;

  if (token) {
    request.headers.Authorization = `Bearer ${token}`;
  }

  return request;
};

servicesInstance.interceptors.request.use(_interceptRequest);
servicesInstance.interceptors.response.use(_interceptResponseSuccess, (error) =>
  _interceptResponseError(error, servicesInstance),
);

smeInstance.interceptors.request.use(_interceptRequest);
smeInstance.interceptors.response.use(_interceptResponseSuccess, (error) =>
  _interceptResponseError(error, smeInstance),
);

certificationInstance.interceptors.request.use(_interceptRequest);
certificationInstance.interceptors.response.use(
  _interceptResponseSuccess,
  (error) => _interceptResponseError(error, certificationInstance),
);

printInstance.interceptors.request.use(_interceptRequest);
printInstance.interceptors.response.use(_interceptResponseSuccess, (error) =>
  _interceptResponseError(error, printInstance),
);

otpInstance.interceptors.request.use(_interceptRequest);
otpInstance.interceptors.response.use(_interceptResponseSuccess, (error) =>
  _interceptResponseError(error, otpInstance),
);

statisticsInstance.interceptors.request.use(_interceptRequest);
statisticsInstance.interceptors.response.use(
  _interceptResponseSuccess,
  (error) => _interceptResponseError(error, statisticsInstance),
);

onboardingInstance.interceptors.request.use(_interceptRequest);
onboardingInstance.interceptors.response.use(
  _interceptResponseSuccess,
  (error) => _interceptResponseError(error, onboardingInstance),
);

onboardingAuthInstance.interceptors.request.use(_interceptRequest);
onboardingAuthInstance.interceptors.response.use(
  _interceptResponseSuccess,
  (error) => _interceptResponseError(error, onboardingAuthInstance),
);

authInstance.interceptors.response.use(
  _interceptResponseSuccess,
  _interceptResponseError,
);

const instances = {
  servicesInstance,
  smeInstance,
  certificationInstance,
  printInstance,
  otpInstance,
  statisticsInstance,
  onboardingInstance,
  authInstance,
  onboardingAuthInstance,
};

export default instances;
